import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { EventSheet } from '../components/EventSheet'
import { EventRow } from '../components/EventRow'
import { DayStrip } from '../components/DayStrip'
import { ScreenHead } from '../components/ScreenHead'
import { Picks } from '../components/Picks'
import { TrainsCard } from '../components/TrainsCard'
import { TasksCard } from '../components/TasksCard'
import { KindHeading } from '../components/KindHeading'
import { Chip, Empty, Field, Row } from '../ui'
import { periods, days, addDays, fmt, placeName } from '../lib/calendar'
import { timeIn } from '../lib/ics'
import { clockIn, hereZone } from '../lib/zone'
import { currentRegion, homeName, homeRegionOf, useRegionsNow } from '../lib/regions'
import { useCurrentRegion, useRegionPlace } from '../lib/currentRegion'
import {
  useAffinity, useCommitments, useEntryState, useFeed, useFlights, useFollows, useNews, useShowings,
  useStays, useVisitors, useWeather,
} from '../lib/queries'
import { secondHomeOf, useProfile } from '../lib/profile'
import { useSeen } from '../lib/seen'
import { useHeldWhileHidden, useOnTab } from '../lib/router'
import type { AffinityMap } from '../lib/affinity'
import {
  alreadyHappened, cityOf, idsOf, inReach, languageAllowed, offFeed, reachOf, talkLanguageAllowed,
} from '../lib/entries'
import { isSoldOut, useSoldOut } from '../lib/tickets'
import { awayFromEvent } from '../lib/away'
import { classicalForm } from '../lib/classicalForm'
import { plateFor } from '../lib/rowMeta'
import { kindOf, type Kind } from '../lib/browse'
import { groupsWhenMixed } from '../lib/perKind'
import { filmsOutLabel, freshOf } from '../lib/newIn'
import { visitorItems } from '../lib/yearItems'
import { routeLabel } from '../lib/flightRoute'
import {
  AHEAD_DAYS, affectsCityToday, berlinDay, cityWindowEnded, dayBlocksToDraw, berlinHour, birthdayName, cityLinesToday, effectiveScore,
  greeting, matchesQuery, onMyLine,
  pickLeads, picksWindow, rowsForDayPlan, rowsOnDay, sectionOf, tonightHeading, worthADrive, type Reach, type Section,
} from '../lib/today'
import { weekAhead } from '../lib/weekAhead'
import { TRAIN_STOPS } from '../lib/trainPlan'
import { spanOf } from '../lib/daylong'
import { scoreOf } from '../lib/rankBase'
import { currentNews, newsDateText } from './City'
import type { Commitment, FeedRow, Flight } from '../lib/types'
import type { DayForecast } from '../lib/weather'
import { Loading, Failed } from './Stub'
import { clampAtWord } from '../../../src/clamp'
import { tidyNewsText } from '../../../src/newsText'

/**
 * Today: what is on, in the order the day asks about it.
 *
 * This was Home, which was a magazine — a rail of six cards, a list peek, a
 * health strip, and 124 news items. H1 replaces the shape of it with a
 * question about time: what is on tonight, what is on this weekend, what have
 * I got coming, what runs out this week. Every one of those is a window, and
 * every window is `today.ts`'s, where `npm test` can see it.
 *
 * **Nothing on this screen is a sentence** (B1, H2). The greeting is one of
 * six templates with its slots filled; every other string is a field of a row
 * or a date formatted. `score_reasons` is not read here, or anywhere.
 *
 * Seven queries, down from eleven: the lists peek is gone (Lists has had its
 * own tab since B3) and the health strip went to Account in 2.3, so
 * `useLists`, `useListItems`, `useRecipes`, `useAlerts` and `useHarvestRuns`
 * are no longer this screen's business. `useShowings` is new and is shared
 * with Browse — it is what tells a film week which of its performances is the
 * one tonight.
 */

/** One identity for "nothing yet", so the memos below do not recompute on a
 *  fresh empty array every render. */
const NONE_YET: never[] = []

/** Same identity trick as `NONE_YET`, for the one query that answers with a
 *  `Set` instead of a list — P3's follows. */
const NO_FOLLOWS: ReadonlySet<string> = new Set()

/** Same trick again, for `useAffinity` (Phase E4, K5) — a still-loading or
 *  failed query lifts nothing rather than holding up a section's order. */
const NONE_AFFINITY: AffinityMap = new Map()

/** The sections still drawn as headed lists — Tonight, then (below the week
 *  ahead's own day blocks) Last days and Openings.
 *
 *  **Tomorrow and This weekend are gone from here, WP 8.2** — not from
 *  `sectionOf`, which still classifies a row into either: a day trip has no
 *  other way to reach the screen (`sectionOf`'s own comment on
 *  `startsInWeekend`), and `SECTION_WHEN`'s `weekend` entry and
 *  `followedEvents`' own dedup still read the bucket. What changed is only
 *  that the screen stopped drawing a heading for it — `weekAhead(now)`'s
 *  day-by-day blocks, below, are what a Wednesday or a Saturday reads from
 *  now, and they start at the same "tomorrow" `sectionOf`'s own `'tomorrow'`
 *  branch does, so that bucket stops finding a heading too, for the same
 *  reason.
 *
 *  **Two others are missing on purpose, WP 6.2.** Coming up is still
 *  `sectionOf`'s own 'coming' bucket underneath, but it draws one line per
 *  item rather than the clustered rows every entry here does, so it is
 *  rendered separately, after New in town — the prototype's own order. City
 *  is drawn separately too: it is news rather than events and has its own
 *  destination. */
const SECTIONS: { key: Extract<Section, 'tonight' | 'lastdays' | 'openings'>; title: string }[] = [
  { key: 'tonight', title: 'Tonight' },
  { key: 'lastdays', title: 'Last days' },
  { key: 'openings', title: 'Openings' },
]

/** At most this many rows in a section, and the score decides which (rule 2).
 *  A section that ran to thirty would be the Browse lane with a different
 *  heading, which is what "All N" is for. */
const SECTION_CAP = 8

/** At most this many rows in the day-selected view (WP 5.2, item 2) — wider
 *  than a section's own cap because tapping a day in the strip is already a
 *  decision to look at one day closely, not a front-page sample of it. */
const DAY_CAP = 40

/** How many news lines Today carries. H4's own number, and already true since
 *  WP 2.7 — what was missing was somewhere for the fourth to go, which is
 *  `#/city` now. */
const CITY_LINES = 3
/** The one city-news line the facts under the greeting may carry: a glance,
 *  cut at a word. 80 and the date and a trip countdown fit two meta lines on
 *  a 360px phone; City has the whole title. */
const NEWS_FACT_MAX = 80

/**
 * The `when` (WP 3.2's `browse.ts`) that shows the same rows as a section's
 * "All N". Tonight and This weekend match exactly. The other three do not,
 * and none is invented here (WP 3.2b's stop condition): Coming up holds only
 * Doug's own commitments and flights (`sectionOf` in `today.ts`), which
 * Browse never lists at all, so no `when` could show "the same rows"; Last
 * days and Openings key off facts — a run closing inside a week, a row typed
 * `opening` — that Browse's vocabulary has no value for. Those three send
 * "All N" to `#/browse` unfiltered.
 */
const SECTION_WHEN: Partial<Record<Section, string>> = { tonight: 'tonight', weekend: 'weekend' }

/** New in town (WP 6.2): four films, five exhibitions, at most. */
const NEW_FILMS_MAX = 4
const NEW_EXHIBITIONS_MAX = 5

/**
 * Something of Doug's own, shaped like a row so that one function decides
 * where everything goes.
 *
 * A commitment, a birthday and a flight are not events and have no score, no
 * venue and no picture — but they occupy a day exactly the way an event does,
 * and `sectionOf` should not have to be told twice which days are this
 * weekend. So they are given a `type` from `PERSONAL_TYPES` and a
 * `run_start`, and go through the same windowing as everything else.
 */
interface Personal {
  id: string
  type: 'commitment' | 'birthday' | 'flight'
  title: string
  run_start: string
  /** The row's second line, or null for a one-line row. Never composed: a
   *  flight's is its own departure time, a commitment's is its note. */
  meta: string | null
  /** The clock a commitment or a flight stated, `HH:MM`, or null where
   *  neither did — Coming up's own fact (WP 6.2), and deliberately not
   *  `meta`: a commitment's `meta` is its *note*, and the prototype's own
   *  Coming up line wants the *time* beside the title instead, which for a
   *  flight happen to be the same value and for a commitment do not. */
  time: string | null
}

/** One line of Coming up (WP 6.2): a commitment, a birthday, a flight, or a
 *  visitor at yours, all reduced to the four fields the prototype's own
 *  `.small` row draws. Built once at render time from `sections`' 'coming'
 *  bucket and from `stays` directly — a visitor is deliberately not a
 *  `Personal` (see `PERSONAL_TYPES`'s own comment in `today.ts`). */
interface ComingItem {
  id: string
  on: string
  title: string
  /** Appended to the title with a middle dot, except for a visitor, whose
   *  nights are said once, in the trailing slot, rather than twice. */
  fact: string | null
  isVisitor: boolean
}

type Item = { kind: 'event'; e: FeedRow } | { kind: 'personal'; p: Personal }

/** A row with `EventRow`'s time plate and none of its machinery — no save, no
 *  swipe, no sheet, because none of those mean anything for a dentist's
 *  appointment. The plate markup is `EventRow.css`'s rather than a second
 *  copy of it: two plates that could disagree about a date format is exactly
 *  the bug at the top of CLAUDE.md. */
function PlateRow({ p, now }: { p: Personal; now: Date }) {
  const plate = plateFor(p, now)
  return (
    <Row
      className="ev-row"
      leading={
        <span className="ev-lead ev-lead--plate" style={{ '--ev-hue': plate.hue } as CSSProperties}>
          <span className="ev-plate">
            <b>{plate.big}</b>
            {plate.small && <i>{plate.small}</i>}
          </span>
        </span>
      }
      title={p.title}
      meta={p.meta}
    />
  )
}

/** The first sentence of a source's own summary (WP 5.2, item 3) — quoting,
 *  not composing (rule 1): `#/city` already prints this text in full, and
 *  this is a substring of it, not a word chosen by this screen. Split on the
 *  first ". " after 40 characters, so a short summary keeps its own full stop
 *  rather than losing it to an early split, and a long one is cut where it
 *  reads as one thought. */
function firstSentence(summary: string): string {
  const cut = summary.indexOf('. ', 40)
  return cut === -1 ? summary : summary.slice(0, cut + 1)
}

/** A single row's own place in time, for sorting the day view (WP 5.2, item
 *  2): a stated clock first, a run's own opening day otherwise, oldest read
 *  from `feed_lite`'s columns directly — the same three fields
 *  `rowMeta.ts`'s `plateDay` reads, in the same order of preference. Score is
 *  the tiebreak `sections`' own sort already uses. */
const timeKey = (e: FeedRow, day: string): string =>
  // A festival or market on this day has no hour on it (`daylong.ts`): its
  // `next_showing` is the next day somebody printed a time for, which can be
  // days away. It leads the day, as an all-day thing does.
  spanOf(e) ? day : e.next_showing ?? e.next_opening ?? e.run_start ?? ''

export function Today() {
  // Read fresh every render rather than pinned at mount: the screen stays
  // mounted for the whole session (X7), and a `useMemo(() => new Date(), [])`
  // would have it still saying "Friday evening" on Saturday. The memos below
  // are keyed on a five-minute bucket rather than the hour (WP 5.2): they used
  // to key on the hour alone, which was enough while the only boundaries that
  // changed what is on screen were 17:00 and 23:00, but `alreadyStarted` now
  // moves a row off the screen 30 minutes after its own start, wherever that
  // falls inside an hour — a bucket the hour cannot see past. The screen does
  // not re-render *at* the boundary either way, it re-renders on the next
  // thing that happens, which for a tab you come back to is arriving on it.
  const now = new Date()
  const today = berlinDay(now)
  // The current region's own minute (C2.1: `hereZone()`, Berlin's until
  // C2.2), not `now.getMinutes()` — that reads the runtime's local zone, which
  // is only the region's by coincidence and would drift a five-minute bucket
  // the moment the device is somewhere the region is not.
  const hereMinute = Number(clockIn(now, hereZone()).slice(3, 5))
  const clockKey = `${today}T${berlinHour(now)}:${Math.floor(hereMinute / 5)}`

  const [open, setOpen] = useState<FeedRow | null>(null)
  const [query, setQuery] = useState('')
  /** The day the strip has selected, or `null` when Today is showing its own
   *  sections (WP 5.2, item 2). */
  const [selectedDay, setSelectedDay] = useState<string | null>(null)
  // A "see the day" tap's scroll, run after the commit that draws the day.
  // Above every early return — see `seeDay`.
  const [dayScroll, setDayScroll] = useState(0)
  useEffect(() => {
    if (!dayScroll) return
    const strip = document.querySelector('.daystrip')
    if (strip) window.scrollTo({ top: strip.getBoundingClientRect().top + window.scrollY - 16 })
  }, [dayScroll])

  const staysQ = useStays()
  const flightsQ = useFlights()
  const commitmentsQ = useCommitments()
  // Today paints from the first page of a cold pull (a1-17): its day blocks,
  // picks, Openings and Coming up read no further than `AHEAD_DAYS`, which is
  // what that page holds. The sections below that look further — New in town
  // (30 days), Worth a drive (six weeks), From places you follow (the whole
  // horizon) — and a search wait for `partial` to clear, because each counts
  // or ranks across rows the first page does not have.
  const feedQ = useFeed({ soonest: true })
  // a4-13: the feed and entry_state are held while Today is hidden — a save on
  // Browse or a feed refetch re-derives Today when it is next shown, not
  // behind its `hidden` div. The rows and whether they are only the first page
  // are held as one value, so `far` can never describe rows Today is not
  // drawing from.
  const shown = useOnTab('today')
  const feedNow = useMemo(
    () => (feedQ.data ? { rows: feedQ.data, partial: feedQ.partial } : undefined),
    [feedQ.data, feedQ.partial])
  const feedHeld = useHeldWhileHidden(feedNow, shown)
  const far = !(feedHeld?.partial ?? feedQ.partial)
  const stateQ = useEntryState()
  const newsQ = useNews()
  const showingsQ = useShowings()
  const followsQ = useFollows()
  const weatherQ = useWeather()
  const visitorsQ = useVisitors()
  // WP 8.4: seen rows leave every event list, the same as dismissed ones.
  const seenQ = useSeen()
  // Phase E4 (K5): Doug's own history with the people and ensembles on a
  // row, folded into the picks, the week ahead and the section sort below.
  const affinityQ = useAffinity()
  // U1.2: the language gates and the lines city news ranks by are the
  // person's own. Above every early return, so a cold load reads it too.
  const profile = useProfile()
  const { english_only: englishOnly, talks_language: talksLanguage } = profile
  // C2.2 (D5): the lines city news ranks by are the current region's place's —
  // the U7 in Berlin, the Fitchburg Line in Concord — not one profile column
  // read in every region. No place, no lines: nothing outranks anything.
  const place = useRegionPlace()
  const transitLines = place?.transit_lines ?? NONE_YET
  // U1.3: what a second-home stay is called ("Away · Ireland") is the profile's.
  const second = secondHomeOf(profile)
  const secondLabel = second?.label.trim() || null
  // C2.1: the region being looked at (reach, the trip line) and the home
  // region (what a `'Home'` period is called, and what "away" is away from).
  // The bundled copy until the tables answer, so a cold load reads the same.
  const regions = useRegionsNow()
  const region = currentRegion(regions)
  const home = homeName(regions, profile)
  const homeId = homeRegionOf(profile)
  const { stay: coveringStay } = useCurrentRegion()
  const where = useMemo(() => ({ home: { id: homeId, name: home }, towns: regions.towns }), [homeId, home, regions.towns])

  const stays = staysQ.data ?? NONE_YET
  const flights = flightsQ.data ?? NONE_YET
  const commitments = commitmentsQ.data ?? NONE_YET
  const feed = feedHeld?.rows ?? NONE_YET
  const stateRows = useHeldWhileHidden(stateQ.data, shown) ?? NONE_YET
  const news = newsQ.data ?? NONE_YET
  const visitors = visitorsQ.data ?? NONE_YET
  const byEntry = showingsQ.data?.byEntry
  const affinity = affinityQ.data ?? NONE_AFFINITY

  /** Seven calendar days, today first, and the forecast keyed by date — a
   *  failed or still-pending fetch is simply an empty map, which is what
   *  makes it "a cell with no weather" rather than a reason to hold up the
   *  strip (`weatherQ.error` is never read). */
  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(today, i)), [today])
  const forecast = useMemo(
    () => new Map((weatherQ.data ?? []).map((d: DayForecast) => [d.date, d])),
    [weatherQ.data])

  // From the start of the year, not from today: clipping the range at today
  // truncates the period you are standing in. Home's own reasoning, and the
  // day strip needs the whole of it to shade the right cells.
  const { current, next, awayOn, periodOn } = useMemo(() => {
    const all = periods(stays, `${today.slice(0, 4)}-01-01`, addDays(today, 730))
    const i = all.findIndex(p => today >= p.start && today <= p.end)
    return {
      current: all[i],
      next: all.slice(i + 1).find(p => p.place !== 'Home'),
      awayOn: (date: string) =>
        all.some(p => p.place !== 'Home' && date >= p.start && date <= p.end),
      // WP 8.2: which period, if any, covers one day of the week ahead — a
      // day block needs the place a trip names, not merely whether one
      // covers the day, which is all `awayOn` above says.
      periodOn: (date: string) => all.find(p => date >= p.start && date <= p.end),
    }
  }, [stays, today])

  const savedIds = useMemo(
    () => new Set(stateRows.filter(r => r.state === 'saved').map(r => r.entry_id)),
    [stateRows])
  const dismissed = useMemo(
    () => new Set(stateRows.filter(r => r.state === 'dismissed').map(r => r.entry_id)),
    [stateRows])
  const seen = seenQ.data

  /**
   * Everything the feed still offers, with the two facts `today.ts` cannot
   * work out for itself attached.
   *
   * `reach` because classifying a city is `reach.ts`'s twenty-name list,
   * which a dependency-free file may not import and must not copy; `saved`
   * because savedness is an `entry_state` row. The spread is once per
   * surviving row inside a memo keyed on the query data, which is the same
   * bargain X8 struck for every other list on this screen.
   *
   * The filters are the ones every list in the app runs: dismissed, seen,
   * language, in reach, not over, not on a day he is somewhere else. A
   * section that offered what Browse refuses would be two apps.
   */
  const live = useMemo(() => {
    const trips = stays.filter(s => s.kind === 'travel' && s.ends_on >= today)
    const awayStays = stays.filter(s => (s.kind === 'travel' || s.kind === 'home2') && s.ends_on >= today)
    return feed
      .filter(e => e.type !== 'news'
        && !dismissed.has(e.id)
        && !seen?.has(e.id)
        && languageAllowed(e, englishOnly)
        && talkLanguageAllowed(e, talksLanguage)
        && !offFeed(e)
        && inReach(e, trips, region, regions.towns)
        && !alreadyHappened(e, today)
        && classicalForm(e) !== 'educational'
        && !awayFromEvent({ city: cityOf(e), run_start: e.run_start, run_end: e.run_end, published_on: e.published_on }, awayStays, where, secondLabel))
      .map(e => ({ ...e, reach: reachOf(cityOf(e), region, regions.towns) as Reach, saved: savedIds.has(e.id) }))
  }, [feed, stays, dismissed, seen, savedIds, today, englishOnly, talksLanguage, secondLabel, region, regions.towns, where])

  /** Doug's own days, in the same shape as the events so one windowing
   *  function covers both. */
  const personal = useMemo<Personal[]>(() => [
    ...commitments.map((c: Commitment) => ({
      id: `c-${c.id}`,
      type: (c.is_birthday ? 'birthday' : 'commitment') as Personal['type'],
      title: c.title,
      run_start: c.on_date,
      meta: c.note,
      time: c.starts_at ? timeIn(c.starts_at, hereZone()) : null,
    })),
    ...flights.map((f: Flight) => ({
      id: `f-${f.id}`,
      type: 'flight' as const,
      title: [f.code, routeLabel(f.from_iata, f.to_iata)].filter(Boolean).join(' · ')
        || 'Flight',
      run_start: f.on_date,
      meta: f.depart_at ? f.depart_at.slice(0, 5) : null,
      time: f.depart_at ? f.depart_at.slice(0, 5) : null,
    })),
  ], [commitments, flights])

  /** N3. Client-side, over the store, on what the row already carries — no
   *  server call, and no match against anything generated. */
  // a1-27, Doug's choice: a card whose every future night is sold out leaves
  // Today's sections, and comes back for a search — Browse's own rule. The
  // sheet still says sold out.
  const soldOutMarks = useSoldOut()
  const matches = useMemo(() => {
    const searching = query.trim() !== ''
    const soldOut = (e: FeedRow) => soldOutMarks.size > 0
      && isSoldOut(e, idsOf(e).flatMap(id => byEntry?.get(id) ?? NONE_YET), soldOutMarks, new Date())
    return {
      events: live.filter(e => matchesQuery(e, query) && (searching || !soldOut(e))),
      personal: personal.filter(p => matchesQuery(p, query)),
    }
  }, [live, personal, query, soldOutMarks, byEntry])

  /**
   * Every row filed under its one section (rule 3), capped, ranked.
   *
   * Doug's own things lead inside a section and the events follow by score:
   * a commitment is not a recommendation and has no score to be ranked by,
   * and something you have already agreed to is the first thing you need to
   * know about the evening.
   */
  /**
   * The picks, and the window they are about (H2, WP 5.3).
   *
   * Both from the clock, and both `today.ts`'s: which rows clear the bar and
   * what the rail is called are the same decision read twice, so they are
   * computed from one `now` here rather than once here and once inside the
   * component.
   */
  const picks = useMemo(
    () => pickLeads(matches.events, now, byEntry, affinity),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `now` is a fresh
    // Date every render; `clockKey` is the part of it these windows depend on.
    [matches.events, byEntry, affinity, clockKey])

  /** The ids the rail is already showing, for the two exclusions below. A
   *  `Set` because there are up to five of them now, and the single-id
   *  comparison this replaces does not generalise. */
  const pickIds = useMemo(() => new Set(picks.map(e => e.id)), [picks])

  const picksLabel = useMemo(
    () => picksWindow(now).label,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as above.
    [clockKey])

  const sections = useMemo(() => {
    const out = new Map<Section, Item[]>()
    const push = (s: Section | null, item: Item) => {
      if (!s) return
      const list = out.get(s)
      if (list) list.push(item); else out.set(s, [item])
    }
    for (const p of matches.personal) push(sectionOf(p, now), { kind: 'personal', p })
    for (const e of matches.events) {
      // A pick is the row, promoted. Leaving it in its section too puts the
      // same event on screen twice within one scroll — the first render had
      // Iván Fischer at the top of the page and again four rows into Last
      // days. The count on "All N" drops with it, which is right: it counts
      // what this section is still holding back. WP 5.3 made this up to five
      // rows rather than one, which is five chances of that duplicate rather
      // than one, and is why the exclusion is a `Set`.
      if (pickIds.has(e.id)) continue
      push(sectionOf(e, now, byEntry?.get(e.id)), { kind: 'event', e })
    }

    for (const [key, list] of out) {
      list.sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'personal' ? -1 : 1
        if (a.kind === 'personal' && b.kind === 'personal') {
          return a.p.run_start < b.p.run_start ? -1 : 1
        }
        const sa = a.kind === 'event' ? effectiveScore(a.e, affinity) : 0
        const sb = b.kind === 'event' ? effectiveScore(b.e, affinity) : 0
        return sb - sa
      })
      out.set(key, list)
    }
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as above.
  }, [matches, byEntry, pickIds, affinity, clockKey])

  /**
   * The week ahead (WP 8.2): one plan per day from `weekAhead(now)`, and the
   * rows `rowsForDayPlan` says belong under it. `full` reruns the same
   * function at `DAY_CAP` instead of the day's own cap — the same trick
   * `renderSection`'s "All N" already plays with `SECTION_CAP` — so `hidden`
   * is "how many more are there", not merely "how many were asked for and
   * not shown", without a second, uncapped copy of the ranking rule to keep
   * in step with the first.
   */
  const dayPlans = useMemo(() => weekAhead(now, hereZone()),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as above.
    [clockKey])

  const dayBlocks = useMemo(() => {
    const eligible = matches.events.filter(e => !pickIds.has(e.id))
    return dayPlans.map(plan => {
      // `periodOn` always answers *a* period — Berlin, when no stay covers
      // the day — so only one that names somewhere else counts as away.
      const period = periodOn(plan.day)
      const away = period && period.place !== 'Home' ? period : undefined
      // An away day shows no rows at all, so it has none to hide either —
      // `rowsForDayPlan` never runs for it. Without this, `shown` would still
      // hold Berlin's own rows for that night (they are real, just not his),
      // and `shownInDayBlocks` below would then treat an id nobody saw as
      // already shown, silently dropping it from Openings and from a
      // followed venue's own section further down the screen.
      if (away) return { plan, shown: [] as typeof eligible, hidden: 0, away }
      const full = rowsForDayPlan(eligible, { ...plan, cap: DAY_CAP }, now, byEntry, affinity)
      const shown = full.slice(0, plan.cap)
      return { plan, shown, hidden: full.length - shown.length, away }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as above.
  }, [dayPlans, matches.events, pickIds, byEntry, affinity, periodOn, clockKey])

  /** Every id a day block is already showing — `followedEvents`' own dedup
   *  (below) and Openings' (in `renderSection`) both need it, for the same
   *  reason `pickIds` excludes the rail's own rows from `sections`: a row
   *  shown once on this screen should not be shown again lower down it. */
  const drawnDayBlocks = useMemo(() => dayBlocksToDraw(dayBlocks, query), [dayBlocks, query])

  const shownInDayBlocks = useMemo(
    () => new Set(dayBlocks.flatMap(b => b.shown.map(e => e.id))),
    [dayBlocks])

  /**
   * P3's own section: what is on at a place Doug follows, ranked by score.
   *
   * Rule 3 of the brief — capped like the others (done in the render below)
   * and excluding rows already surfaced in Tonight or a day block, so a
   * followed venue's own concert tonight is not shown twice on one screen.
   * `feed_lite`'s `building_id` is already the hall's *building* (its
   * `parent_id`, or its own id where it has none — migration 20260921100000),
   * so a hall under a followed building matches without a second lookup.
   */
  const followedEvents = useMemo(() => {
    const followed = followsQ.data ?? NO_FOLLOWS
    if (!followed.size) return NONE_YET
    const shown = new Set(shownInDayBlocks)
    for (const item of sections.get('tonight') ?? []) if (item.kind === 'event') shown.add(item.e.id)
    return matches.events
      .filter(e => !pickIds.has(e.id) && !shown.has(e.id))
      .filter(e => {
        const place = e.building_id ?? e.venue_id
        return place != null && followed.has(place)
      })
      .sort((a, b) => scoreOf(b) - scoreOf(a))
  }, [followsQ.data, sections, shownInDayBlocks, matches.events, pickIds])

  /**
   * Visitors at yours whose stay falls in the next `AHEAD_DAYS` (WP 6.2, now
   * on 6.5's own table). `yearItems.ts`'s `visitorItems` is deliberately kept
   * out of `yearItems()` itself — its own header explains why: `yearItems()`
   * feeds both a `PeriodCard`'s date-range slice and this list, and folding a
   * visitor's own item into that one pool drew it twice on the Year (once
   * inside a period card, once as its own `VisitorCard`). So this calls
   * `visitorItems` itself rather than asking `yearItems()` for one, and reads
   * it by calendar year — a visit starting in the last `AHEAD_DAYS` of
   * December needs next year's call too, which is what `years` is for. A
   * span, not a single occurrence, so it is filtered on its own `on` date
   * (its arrival) rather than run through `sectionOf`: a visit already under
   * way still belongs in Coming up, and `sectionOf`'s `ahead` check only ever
   * looks forward from today.
   */
  const comingVisitors = useMemo(() => {
    const to = addDays(today, AHEAD_DAYS)
    const years = new Set([Number(today.slice(0, 4)), Number(to.slice(0, 4))])
    return [...years]
      .flatMap(y => visitorItems(visitors, y))
      .filter(v => v.on >= today && v.on <= to)
  }, [visitors, today])

  /**
   * Coming up (WP 6.2): one line per item, not the clustered rows every other
   * section draws — a dentist's appointment and a visitor's stay are neither
   * one worth a kind heading over. `sections`' own 'coming' bucket already
   * excludes anything Tonight or This weekend claimed first (rule 3); this
   * merely reduces it, and the visitors, to the four fields the row needs.
   */
  const comingUp = useMemo<ComingItem[]>(() => {
    const personalItems: ComingItem[] = (sections.get('coming') ?? [])
      .filter((i): i is Extract<Item, { kind: 'personal' }> => i.kind === 'personal')
      .map(i => ({ id: i.p.id, on: i.p.run_start, title: i.p.title, fact: i.p.time, isVisitor: false }))
    const visitorLines: ComingItem[] = comingVisitors.map(v => ({
      id: v.id, on: v.on, title: v.title, fact: v.fact ?? null, isVisitor: true,
    }))
    return [...personalItems, ...visitorLines].sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : 0))
  }, [sections, comingVisitors])

  /** Worth a drive (blind spots A3): up to three day trips starting in the
   *  next six weeks, after the week ahead. `today.ts` decides which; this
   *  drops what the rail or a day block already shows first, as Openings
   *  and From places you follow do — a Sunday in Potsdam under Sunday and
   *  again two sections down is the duplicate listing, and the three places
   *  go to trips the screen has not mentioned. */
  const drive = useMemo(
    () => worthADrive(
      matches.events.filter(e => !pickIds.has(e.id) && !shownInDayBlocks.has(e.id)), today, affinity),
    [matches.events, pickIds, shownInDayBlocks, today, affinity])

  /**
   * New in town (WP 6.2): 6.0's `isNew`/`newOn` over the live rows, plus
   * nothing else — no reach or day-trip rule, no "already started", because a
   * film that opened last week is still new whatever else is true of it
   * today. Sorted newest first, the prototype's own order, so the four films
   * and five exhibitions shown are the most recent of however many qualify.
   */
  const fresh = useMemo(() => freshOf(matches.events, today), [matches.events, today])

  const freshFilms = useMemo(() => fresh.filter(x => x.kind === 'film').slice(0, NEW_FILMS_MAX), [fresh])
  const filmsOut = filmsOutLabel(freshFilms.map(x => x.on), today)
  const freshExhibitions = useMemo(() => fresh.filter(x => x.kind === 'exhibition'), [fresh])
  const freshExhibitionsShown = useMemo(
    () => freshExhibitions.slice(0, NEW_EXHIBITIONS_MAX), [freshExhibitions])

  /**
   * The one day the strip has picked, in place of the sections (WP 5.2, item
   * 2): "when I click on one of those dates I'm brought over to Browse — my
   * expected behaviour would be that I actually see the events for that day
   * come up underneath" (Doug, 26 Sept). `rowsOnDay` is `sectionOf`'s own
   * sense of "on that day" (rule 3), read against the query-filtered lists so
   * the search box still narrows it. Personal rows lead, unclustered, the same
   * arrangement every section already draws; the events are sorted by time
   * and then score, because a day you are looking at closely is read
   * chronologically, not by what the profile liked best.
   */
  const dayView = useMemo(() => {
    if (!selectedDay) return NONE_YET
    const personalItems: Item[] = rowsOnDay(matches.personal, selectedDay, now)
      .map(p => ({ kind: 'personal' as const, p }))
    const eventItems: Item[] = rowsOnDay(matches.events, selectedDay, now, byEntry)
      .sort((a, b) => timeKey(a, selectedDay).localeCompare(timeKey(b, selectedDay)) || scoreOf(b) - scoreOf(a))
      .map(e => ({ kind: 'event' as const, e }))
    return [...personalItems, ...eventItems]
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as above.
  }, [selectedDay, matches, byEntry, clockKey])

  /**
   * H4's three lines, ranked rather than merely recent: what still affects
   * today first, a transport closure first among that (UI audit a1-23, Doug's
   * choice) — `today.ts`'s `cityLinesToday`, over whatever `useNews` still
   * calls current. A march whose hours are over for the day is dropped here
   * and stays on `#/city`, so this reads the clock (`clockKey`). The search
   * narrows them like everything else — a query that emptied the events and
   * left three unrelated disruptions standing would be the screen not
   * answering what was asked — matched against the English headline where
   * 6.6 wrote one, since that is the text on screen.
   */
  const cityLines = useMemo(
    () => cityLinesToday(currentNews(news, today), now, transitLines)
      .filter(n => matchesQuery({ title: n.title_en ?? n.title }, query))
      .slice(0, CITY_LINES),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as above.
    [news, today, query, clockKey, transitLines])

  /**
   * C4.2: the alert the trains card carries, if one is on the line it draws —
   * `onMyLine` and `affectsCityToday`, the City lane's own two tests, so a
   * delay the lane calls today's is the delay the card calls today's. The
   * lane keeps drawing it too (the card does not take it away).
   */
  const trainInfo = place?.transit_stop ? TRAIN_STOPS.get(place.transit_stop) : undefined
  const trainAlert = useMemo(
    () => trainInfo
      ? currentNews(news, today).find(n => n.category === 'transport' && onMyLine(n, [trainInfo.route])
          && affectsCityToday(n, today) && !cityWindowEnded(n, now)) ?? null
      : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as above.
    [news, today, clockKey, trainInfo])

  const pending = staysQ.isPending || flightsQ.isPending || commitmentsQ.isPending
    || feedQ.isPending || stateQ.isPending || newsQ.isPending
    // A search answers over the whole horizon or says "Nothing matching".
    || (!far && !!query && !feedQ.error)
  const error = staysQ.error ?? flightsQ.error ?? commitmentsQ.error ?? feedQ.error
    ?? stateQ.error ?? newsQ.error

  // The greeting and the strip need no feed, so they are drawn before it
  // arrives and the skeletons stand in for the sections alone — the screen
  // does not blink between a spinner and a page.
  //
  // WP 10.7 (10.6 finding, per 10.1's own report): this early return draws
  // before `ScreenHead` mounts, so a failed load left the account button
  // unreachable — the greeting is not computed yet at this point (it needs
  // `commitments`, read below), so the head gets the screen's plain name
  // rather than `g.line`.
  if (error) return <><ScreenHead lead={<h1 className="today__greeting">Today</h1>} /><Failed error={error.message} /></>

  const birthdayToday = commitments.find(c => c.is_birthday && c.on_date === today)
  const awayPlace = current && current.place !== 'Home' ? current.label ?? placeName(current, second, home) : null
  const g = greeting(now, {
    away: awayPlace,
    birthday: birthdayToday ? birthdayName(birthdayToday.title) : null,
  })

  // The date, then at most two facts. The trip and the one city-news line
  // flagged today (H4) are the two `greeting` cannot know; it supplies the
  // date and the birthday where the line did not already carry it.
  //
  // The countdown is the one thing the where-you-are card said that the
  // greeting does not, and Doug asked for it by name on 21 September: "a card
  // that says two days are left tells him something he would otherwise have
  // to count." Inclusive of today — on the last day of a stay there is one
  // more day in it, not none — and it does not repeat the place, which the
  // line above it has already named.
  const daysLeft = awayPlace && current ? days(today, current.end) + 1 : null
  const untilNext = next ? days(today, next.start) : null
  // `affectsCityToday`, the same test the City lines are ranked by — so the
  // fact is the first of them that is about today, a closure before a march.
  const todayNews = cityLines.find(n => affectsCityToday(n, today))
  const facts = [
    ...g.sub.slice(1),
    daysLeft == null ? null : daysLeft === 1 ? 'Last day' : `${daysLeft} more days`,
    untilNext == null ? null
      : `${next!.label ?? placeName(next!, second, home)} in ${untilNext} day${untilNext === 1 ? '' : 's'}`,
    // Clamped here, at the one place that draws it as a fact: City draws the
    // same title whole, and a Polizei Thema is whatever the organiser typed
    // — 315 characters of it reached this line on 2 October, after the date
    // and a trip countdown, and ran to nine lines on the phone. The Worker
    // now caps a Polizei title at 90 (`splitThema`); this is the app's own
    // guarantee for every source, and for rows written before that.
    todayNews ? clampAtWord(tidyNewsText(todayNews.title_en ?? todayNews.title), NEWS_FACT_MAX) : null,
  ].filter(Boolean).slice(0, 2)

  // Not home: the place's own label ("Concord") if the person has one in this
  // region, else the region's name. `coveringStay` is the stay covering today,
  // whichever region it is in.
  const stayHere = coveringStay && coveringStay.region_id === region.id ? coveringStay : null
  // The greeting already says "in Concord" when the stay is labelled that, so
  // the line under it does not say the place again: just "until 8 Dec", or
  // nothing at all when there is no end to give (C2.3, a leftover of C2.2).
  const regionPlace = place?.label ?? region.name
  const greetingSaysIt = awayPlace != null && awayPlace.trim().toLowerCase() === regionPlace.trim().toLowerCase()
  const until = stayHere ? `until ${fmt(stayHere.ends_on, { day: 'numeric', month: 'short' })}` : null
  const regionLine = region.id === homeId ? null
    : greetingSaysIt
      ? (until ? until[0].toUpperCase() + until.slice(1) : null)
      : [regionPlace, until].filter(Boolean).join(' · ')

  const go = (hash: string) => { window.location.hash = hash }

  /**
   * H3's tap, WP 5.2's own version: the strip selects a day and Today shows
   * it (item 2), rather than scrolling to a section or leaving for Browse —
   * `/browse?day=<date>` no longer exists anywhere in the app (WP 8.9 removed
   * the day view's own "All N" link to it, dead since 8.3: see that section's
   * own comment).
   *
   * Tapping today's cell or the already-selected cell clears the selection,
   * which is the only way back to the sections short of the tab bar; every
   * other cell replaces whichever day was picked, since only one is ever
   * shown at once.
   */
  function pickDay(date: string) {
    setSelectedDay(d => (date === today || date === d) ? null : date)
  }

  /** A day block's "N more · see the day" (UI audit a1-19). The same
   *  selection the strip makes, but always a selection — never the toggle
   *  `pickDay` is — and from halfway down the page: the day view replaces
   *  everything under the strip, so the scroll position the tap left behind
   *  landed on the day's third kind heading with the strip and the day's own
   *  heading above the fold. The scroll waits for the commit that draws the
   *  day (`dayScroll`, above the early returns), since before it the page is
   *  still the sections. */
  function seeDay(date: string) {
    setSelectedDay(date)
    setDayScroll(n => n + 1)
  }

  /** `SECTIONS` split where New in town goes between them (the prototype's
   *  own position, "after the weekend section", now after the week ahead's
   *  own day blocks) — see `renderSection` below for what each half draws. */
  const frontSections = SECTIONS.filter(s => s.key === 'tonight')
    .map(s => ({ ...s, title: tonightHeading(now) }))
  const backSections = SECTIONS.filter(s => s.key !== 'tonight')

  /** One of `SECTIONS`' own entries, clustered and capped — the anatomy every
   *  section but Coming up and the week ahead's own day blocks share.
   *
   *  Openings' own list drops anything a day block already showed
   *  (`shownInDayBlocks`) — an opening inside the horizon is already under
   *  its own night, and a second copy of it here would be exactly the
   *  duplicate-listing bug CLAUDE.md keeps naming (the Iván Fischer row
   *  `pickIds` exists to stop, one section further down). Last days never
   *  needs the same filter: a run only ever reaches a day block by opening
   *  inside the horizon, and Last days only ever holds one that was already
   *  open before today — the two can't name the same row. */
  function renderSection({ key, title }: { key: Section; title: string }) {
    const raw = sections.get(key)
    const list = key === 'openings'
      ? raw?.filter(item => item.kind === 'personal' || !shownInDayBlocks.has(item.e.id))
      : raw
    if (!list?.length) return null
    return (
      <section key={key} id={`t-${key}`} className="tsec">
        <div className="tsec__head">
          <h2>{title}</h2>
          {/* "All N" counts what the section is a sample of. Tonight sends
              Browse the matching `when` (`SECTION_WHEN`); Last days and
              Openings have none that shows the same rows and land on Browse
              unfiltered. */}
          {list.length > SECTION_CAP && (
            <button
              className="more"
              onClick={() => go(SECTION_WHEN[key] ? `/browse?when=${SECTION_WHEN[key]}` : '/browse')}
            >All {list.length}</button>
          )}
        </div>
        {clusteredRows(list.slice(0, SECTION_CAP))}
      </section>
    )
  }

  /**
   * One block of the week ahead (WP 8.2): the weekday and date, then that
   * day's own rows from `dayBlocks` — or, on a day Doug is away, the place
   * and nothing else, since Berlin's events are not his that night.
   *
   * Grouped by kind whenever the rows shown hold more than one kind, flat
   * when they hold one (`eventRows`, UI audit a1-24, Doug's choice) — the
   * same rule the picked day follows. It used to group only at
   * `WEEKEND_AHEAD_CAP`, which drew one day flat and the next in kind groups.
   * Not `clusteredRows`, which also draws personal rows this screen's day
   * blocks never carry (Coming up already has Doug's own days, further down).
   */
  function renderDayBlock({ plan, shown, hidden, away }: (typeof dayBlocks)[number]) {
    if (!shown.length && !away) return null
    return (
      <section key={plan.day} className="tsec">
        <div className="tsec__head">
          <h2>{fmt(plan.day, { weekday: 'long', day: 'numeric', month: 'short' })}</h2>
          {/* "N more · see the day", not "All N": there is no `when=` for one
              day, so this selects the day in the strip instead of linking to
              Browse — `.tsec__head .more` is the same link style every other
              section's "All N" already uses. `hidden` is always 0 on an away
              day (above), so this never shows one for a night with no rows. */}
          {hidden > 0 && (
            <button className="more" onClick={() => seeDay(plan.day)}>{hidden} more · see the day</button>
          )}
        </div>
        {away ? (
          <p className="kindhead">Away · {away.label ?? placeName(away, second, home)}</p>
        ) : eventRows(shown)}
      </section>
    )
  }

  /**
   * Event rows under kind headings when they are of more than one kind, flat
   * when they are of one — `perKind.ts`'s `groupsWhenMixed`, the one rule
   * every day block and section on this screen draws by (a1-24).
   *
   * **6.0's `clusterByKind` underneath (WP 6.2), not Browse's own.** `kindOf`
   * is still `browse.ts`'s — it is the chip vocabulary `KindHeading` already
   * draws (`KIND_LABEL`/`KIND_DOT`, `Exclude<Kind, 'all'>`) and the one every
   * row's own `kindWord` agrees with, tour included. `perKind.ts` is not
   * generic the way Browse's is, so `c.rows` comes back typed to the small
   * shape it reads — the objects are still full `FeedRow`s, which is what the
   * cast asserts rather than guesses.
   */
  function eventRows(rows: FeedRow[]) {
    const groups = groupsWhenMixed(rows, { kindOf: row => kindOf(row as FeedRow) })
    if (!groups) return rows.map(e => <EventRow key={e.id} e={e} showings={byEntry?.get(e.id)} onOpen={setOpen} />)
    return groups.map(c => {
      const crows = c.rows as FeedRow[]
      return (
        <div key={c.kind}>
          <KindHeading kind={c.kind as Exclude<Kind, 'all'>} count={crows.length} />
          {crows.map(e => (
            <EventRow key={e.id} e={e} showings={byEntry?.get(e.id)} under={c.kind as Exclude<Kind, 'all'>}
                      onOpen={setOpen} />
          ))}
        </div>
      )
    })
  }

  /**
   * Personal rows first and unclustered, then the events clustered by kind
   * (WP 5.0's heading, item 4 of the README's table: "a section that mixes
   * kinds clusters them") — with no heading at all where a section holds only
   * one kind, which is most of them most nights. Shared between every section
   * below and the day view, which draw the same anatomy at two different caps.
   * The events go through `eventRows`, the same rule the week ahead's day
   * blocks draw by.
   */
  function clusteredRows(items: Item[]) {
    const personalItems = items.filter((i): i is Extract<Item, { kind: 'personal' }> => i.kind === 'personal')
    const eventItems = items
      .filter((i): i is Extract<Item, { kind: 'event' }> => i.kind === 'event')
      .map(i => i.e)
    return (
      <>
        {personalItems.map(item => <PlateRow key={item.p.id} p={item.p} now={now} />)}
        {eventRows(eventItems)}
      </>
    )
  }

  return (
    <>
      {/* WP 10.1: the greeting is the screen's first row now, with the
          account button beside it; the date line stays under the row, so a
          greeting that wraps keeps the button beside its first line. */}
      <ScreenHead lead={<h1 className="today__greeting">{g.line}</h1>} />
      {/* C2.2: where the app is looking, only when that is not home — "Concord
          · until 8 Dec". The stay's end is read only when the stay is in
          this very region (a Rome weekend inside a Boston stay is not the
          Boston stay), and an override with no stay under it says no end.
          Its own class: `.today__sub` carries the 16px that belongs under the
          last header line, and this one sits 4px over the date line. */}
      {regionLine && <p className="today__region">{regionLine}</p>}
      <p className="today__sub">{[g.sub[0], ...facts].join(' · ')}</p>

      {/* C4.2: the next trains from the place's stop, only where the current
          region's place has one (Concord; never Berlin). Before the search
          box, under the greeting, where the brief puts it. */}
      {place?.transit_stop && (
        <TrainsCard
          place={place} active={shown}
          alert={trainAlert ? {
            title: tidyNewsText(trainAlert.title_en ?? trainAlert.title),
            meta: [
              newsDateText(trainAlert, today),
              (trainAlert.summary_en ?? trainAlert.summary)
                ? firstSentence(tidyNewsText((trainAlert.summary_en ?? trainAlert.summary)!)) : null,
            ].filter(Boolean).join(' · ') || null,
            onPress: () => go('/city'),
          } : null}
        />
      )}

      {/* Tasks plan T3 (D10): what is overdue or due today, with the count
          and an add field. After the trains and before the search, since a
          thing due today outranks a suggestion. Reads its own queries and
          draws nothing until they answer. */}
      <TasksCard />

      {/* N3. `Field.Input` without a `Field` around it: the primitive supports
          it and owes an `aria-label` in return, which is the right trade on a
          front page where a "Search" label above the box would be a third
          line of furniture before the first event. */}
      <form
        className="today__search"
        onSubmit={ev => {
          ev.preventDefault()
          // A blank submit is not a filter — `?q=` with nothing in it would
          // be a stray param Browse then has to treat as "matches everything"
          // anyway. Encoded so "Bach & Händel" survives inside the hash.
          const q = query.trim()
          go(q ? `/browse?q=${encodeURIComponent(q)}` : '/browse')
        }}
        role="search"
      >
        <Field.Input
          type="search"
          value={query}
          onChange={ev => setQuery(ev.currentTarget.value)}
          aria-label="Search events, people and places"
          placeholder="Search events, people, places"
        />
      </form>

      <DayStrip dates={weekDates} forecast={forecast} away={awayOn} today={today} selected={selectedDay} onPick={pickDay} />

      {pending ? (
        <Loading variant="row" count={5} />
      ) : selectedDay ? (
        // WP 5.2, item 2: a day selected replaces the picks rail and every
        // section with one section of its own — the day's own rows, not a
        // sample of them, which is what the higher cap says. WP 8.9 removed
        // the "All N" link this used to draw past `DAY_CAP`: it went to
        // `/browse?day=<date>`, and 8.8 proved that query param does nothing
        // in Browse but force `when` to Any time — `browse.ts` has no
        // "which day is this row on" a filter could key off, so the link
        // never delivered "all of this day", only "everything, unscoped".
        // No live day reaches `DAY_CAP` (40) today, so nobody has tapped a
        // dead link yet; removing the affordance rather than leaving a
        // promise nothing can keep. Don't re-add it without giving
        // `browse.ts` a day filter to honour first.
        //
        // a1-18, Doug's choice: a "Back to today" chip beside the day's own
        // heading — before it, the way back was re-tapping the picked cell or
        // today's, which nothing on screen said. Drawn on an empty day too,
        // which is where it is needed most. A `Chip` for its 36px face and
        // 44px target, but an action, not a filter: no `aria-pressed`.
        <section className="tsec">
          <div className="tsec__head tsec__head--day">
            <h2>{fmt(selectedDay, { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
            <Chip aria-pressed={undefined} onClick={() => setSelectedDay(null)}>Back to today</Chip>
          </div>
          {dayView.length === 0
            ? <Empty title={`Nothing on ${fmt(selectedDay, { weekday: 'long' })}.`} />
            : clusteredRows(dayView.slice(0, DAY_CAP))}
        </section>
      ) : (
        <>
          <Picks picks={picks} label={picksLabel} showings={byEntry} onOpen={setOpen} />

          {/* Tonight — then the week ahead's own day blocks (WP 8.2), then
              New in town, the prototype's own order, then Last days and
              Openings. Split rather than one `.map()`: New in town is not
              one of `sectionOf`'s own sections and has no fixed place in
              `SECTIONS` to be inserted at, so the render does by position
              what the array cannot say. */}
          {frontSections.map(renderSection)}

          {/* The week ahead (WP 8.2): "if it's Thursday or Friday I want more
              of a full weekend programme... Sunday evening I really want a
              glance of the week ahead" (Doug). One block per day from
              `weekAhead(now)`, replacing the old Tomorrow and This weekend
              sections — both of which started at the same "tomorrow"
              `weekAhead` does, and neither of which reached past it. */}
          {drawnDayBlocks.map(renderDayBlock)}

          {/* Blind spots A3: the trips, after the weekend they would fill.
              Plain on purpose — the same rows and a section heading; Doug
              designs the trip card later. Not drawn when empty. */}
          {far && drive.length > 0 && (
            <section id="t-drive" className="tsec">
              <div className="tsec__head"><h2>Worth a drive</h2></div>
              {drive.map(e => (
                <EventRow key={e.id} e={e} showings={byEntry?.get(e.id)} onOpen={setOpen} />
              ))}
            </section>
          )}

          {/* New in town (WP 6.2): films first, then exhibitions, six.0's
              `isNew`/`newOn` and nothing else. No restaurant card — that slot
              waits on a source (README, Needs Doug). */}
          {far && fresh.length > 0 && (
            <section className="tsec">
              <div className="tsec__head">
                <h2>New in town</h2>
                {fresh.length > freshFilms.length + freshExhibitionsShown.length && (
                  // The search goes along (a1-16): `fresh` is narrowed by it,
                  // so the page has to be too for "All N" to land on N.
                  <button className="more" onClick={() => go(query.trim()
                    ? `/browse?new=1&q=${encodeURIComponent(query.trim())}` : '/browse?new=1')}>
                    All {fresh.length}
                  </button>
                )}
              </div>
              {freshFilms.length > 0 && (
                <>
                  <p className="kindhead">
                    Films · {filmsOut!.word} {fmt(filmsOut!.on, { weekday: 'short', day: 'numeric' })}
                  </p>
                  {freshFilms.map(x => (
                    <EventRow key={x.e.id} e={x.e} showings={byEntry?.get(x.e.id)} under="film" onOpen={setOpen} />
                  ))}
                </>
              )}
              {freshExhibitionsShown.length > 0 && (
                <>
                  <p className="kindhead">Exhibitions opening · {freshExhibitions.length}</p>
                  {freshExhibitionsShown.map(x => (
                    <EventRow key={x.e.id} e={x.e} showings={byEntry?.get(x.e.id)} under="exhibition" onOpen={setOpen} />
                  ))}
                </>
              )}
            </section>
          )}

          {backSections.map(renderSection)}

          {/* Coming up (WP 6.2): one line per item — a commitment, a
              birthday, a flight or a visitor at yours — never the clustered
              rows an event section draws. No "All N": none of these are
              Browse's vocabulary (`SECTION_WHEN`'s own comment). */}
          {comingUp.length > 0 && (
            <section className="tsec">
              <div className="tsec__head"><h2>Coming up</h2></div>
              {comingUp.slice(0, SECTION_CAP).map(item => {
                const away = days(today, item.on)
                return (
                  <div key={item.id} className="comingrow">
                    <span className="comingrow__d">{fmt(item.on, { weekday: 'short', day: 'numeric' })}</span>
                    <span className="comingrow__t">
                      {item.title}{item.fact && !item.isVisitor ? ` · ${item.fact}` : ''}
                    </span>
                    <span className="comingrow__x">
                      {item.isVisitor ? item.fact : away <= 0 ? 'today' : `in ${away} day${away === 1 ? '' : 's'}`}
                    </span>
                  </div>
                )
              })}
            </section>
          )}

          {/* P3. Not in `sectionOrder`: this one is not a time window, it is a
              standing preference, so it always renders last among the events
              rather than being placed by `sectionOf`. */}
          {far && followedEvents.length > 0 && (
            <section className="tsec">
              <div className="tsec__head">
                <h2>From places you follow</h2>
                {followedEvents.length > SECTION_CAP && (
                  <button className="more" onClick={() => go('/browse')}>All {followedEvents.length}</button>
                )}
              </div>
              {followedEvents.slice(0, SECTION_CAP).map(e => (
                <EventRow key={e.id} e={e} showings={byEntry?.get(e.id)} onOpen={setOpen} />
              ))}
            </section>
          )}

          {/* H4. Three lines and a way to the rest — the section was already
              capped at three in WP 2.7; `#/city` is where the fourth went. */}
          {cityLines.length > 0 && (
            <section className="tsec">
              <div className="tsec__head">
                <h2>City</h2>
                <button className="more" onClick={() => go('/city')}>More</button>
              </div>
              {/* WP 5.2, item 3: "too small to know what this is" — the date,
                  the transport lines and the source's own first sentence, so
                  a headline like "End of summertime" has something under it
                  before you tap through. Quoting `n.summary`, not composing
                  (rule 1) — `#/city` already prints the whole of it; this is
                  a substring, one line, ellipsis if it still overruns. */}
              {cityLines.map(n => {
                // The English line from 6.6's column when present, else the
                // German with no marker — Doug reads City daily and cannot
                // read German, and a tag saying "translated" on every line
                // would be a fact about the pipeline, not about the city.
                // `tidyNewsText` (UI audit a1-23): rows stored before the
                // Worker learned to drop an empty place or join a word the
                // source broke at a line end still say "07:15–08:00, ·
                // Aufzug" and "Demonstrat- ion", here and in the cache.
                const summary = n.summary_en ?? n.summary
                const meta = [
                  newsDateText(n, today),
                  n.lines.length > 0 ? n.lines.join(', ') : null,
                  summary ? firstSentence(tidyNewsText(summary)) : null,
                ].filter(Boolean).join(' · ')
                return (
                  <Row
                    key={n.id}
                    className="cityrow"
                    leading={null}
                    title={tidyNewsText(n.title_en ?? n.title)}
                    meta={meta || null}
                    onPress={() => go('/city')}
                  />
                )
              })}
            </section>
          )}

          {/* Every section can be empty at once — a Tuesday in Wroclaw with
              nothing booked is a real state of this screen, and it should say
              so rather than end after the day strip. */}
          {!picks.length && SECTIONS.every(s => !sections.get(s.key)?.length)
            && drawnDayBlocks.length === 0
            && fresh.length === 0 && comingUp.length === 0
            && followedEvents.length === 0 && cityLines.length === 0 && (
            <Empty title={query.trim() ? `Nothing matching “${query.trim()}”.` : 'Nothing on in the next two weeks.'} />
          )}
        </>
      )}

      <EventSheet e={open} onClose={() => setOpen(null)} />
    </>
  )
}
